import SpineWrapper from '@Core/Animation/SpineWrapper';
import { GameScene } from '@Core/GameScene/GameScene';
import ReelSymbol from '@Core/Reels/ReelSymbol';
import { ESymbolAnimations } from '@Core/Reels/SymbolAnimations';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { IWheelConfiguration } from '@GFTypes/GameStoreTypes';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { EWildWheelFeatureStates } from './States/WildWheelFeatureStates';
import { WildWheelFeature } from './WildWheelFeature';
import gsap from 'gsap';

export class WildWheelInstance {
    private readonly gameStore: GameStore;
    public spine: SpineWrapper;
    public tween: gsap.core.Tween;
    public wildWheelConfig: IWheelConfiguration;
    public assignedReelIndex: number;
    public assignedWild: ReelSymbol | undefined;
    public assignedMultiplier: number | undefined;
    public newSpinStarted: boolean = false;
    public isBonusWheel: boolean = false;
    constructor(
        private readonly scene: GameScene,
        private readonly manager: WildWheelFeature,
        private readonly instanceIndex: number
    ) {
        this.gameStore = manager.gameStore;
        Logger.log(LoggerLevel.Constructor, { class: WildWheelInstance.name, source: LoggerSource.Game, message: 'WildWheelInstance constructor' });
        EventsCenter.on(EGameStates.IDLE_STATE, this.onIdle, this);
        EventsCenter.on(EGameEvents.SKIP, this.onSkip, this);
        EventsCenter.on(EWildWheelFeatureStates.WILD_WHEEL_START, this.start, this);
        EventsCenter.on(EWildWheelFeatureStates.WILD_WHEEL_END, this.end, this);
        EventsCenter.on(EWildWheelFeatureStates.WILD_WHEEL_MERGE_START, this.onMergeStart, this);
        EventsCenter.on(EWildWheelFeatureStates.WILD_WHEEL_MERGE_END, this.onMergeEnd, this);

        EventsCenter.on(EWildWheelFeatureStates.WILD_WHEEL_HIDDEN, this.hideWheel, this);

        this.wildWheelConfig = this.gameStore.get(EGameStoreKeys.WildWheelConfiguration).wheels[instanceIndex];
        this.instanceIndex = instanceIndex;
        this.assignedReelIndex = this.wildWheelConfig.assignedReel;
        this.onCreate();
    }

    public onCreate(): void {
        const { spineName, skinName, isBonus } = this.wildWheelConfig;
        this.spine = new SpineWrapper(
            this.scene,
            {
                x: this.scene.screen.width / 2,
                y: this.scene.screen.height / 2
            },
            { dataKey: spineName, skinName },
            true,
            false
        )!;

        this.spine.eventManager.registerBlocker('ensureWinWithMultiplier', () => this.manager.isWinStateWithoutWheel.bind(this.manager)());
        if (!isBonus) this.spine.eventManager.registerBlocker('ensureWild', this.checkWild.bind(this));
    }

    public onIdle(): void {
        this.spine.spine.skeleton.setupPose();
        this.newSpinStarted = true;
    }

    public onSkip() {
        if (this.gameStore.get(EGameStoreKeys.CurrentGameSubState) !== EGameStates.WILD_WHEEL_STATE) return;
        if (!this.gameStore.get(EGameStoreKeys.WildWheelConfiguration).skippable) return;
        this.tween?.progress(1);
    }

    private start(): void {
        this.assignedWild = this.manager.getWildForInstance(this.assignedReelIndex);
        this.assignedMultiplier = this.manager.getMultiplierForInstance(this.instanceIndex);
        const isWildWheel = this.assignedWild && this.assignedMultiplier;
        this.isBonusWheel = !!(this.gameStore.get(EGameStoreKeys.SpinOutcome).steps[0].bonusWheelMultiplier && this.assignedMultiplier);

        if (isWildWheel || this.isBonusWheel) {
            if (isWildWheel) {
                const { flyFromSymbol } = this.wildWheelConfig;
                const { inDuration } = flyFromSymbol ?? {};
                if (inDuration) this.fly(true, this.assignedWild!);
            }
            this.newSpinStarted = false;
            this.showWheel();
        }
    }

    private end(): void {
        if (!this.assignedWild) return;
        this.applyMultiplierToWild();
        const { flyFromSymbol } = this.wildWheelConfig;
        const { outDuration } = flyFromSymbol ?? {};
        if (outDuration) {
            this.fly(false, this.assignedWild);
        }
    }

    private onMergeStart(): void {
        this.applyMultiplierToWild();
    }

    private onMergeEnd(): void {
        this.applyMultiplier(this.spine, this.assignedMultiplier ?? 2, true);
    }

    private showWheel(): void {
        const isWildWheel = this.assignedWild && this.assignedMultiplier;
        if (!isWildWheel && !this.isBonusWheel) return;

        const { flyFromSymbol, delay } = this.wildWheelConfig;

        if (delay && this.manager.getWilds().length > 1) {
            this.scene.time.delayedCall(delay, () => {
                this.applyMultiplier(this.spine, this.assignedMultiplier!);
            });
        } else {
            this.applyMultiplier(this.spine, this.assignedMultiplier!);
        }

        this.spine.spine.visible = true;

        if (flyFromSymbol && this.assignedWild?.parent) {
            const scale = this.assignedWild.scale.x * this.assignedWild.parent?.scale.x;
            this.spine.setScale(scale, scale);
            this.assignedWild.visible = false;
        }
    }

    private hideWheel(): void {
        if (!this.assignedWild) return;

        const { flyFromSymbol } = this.wildWheelConfig;

        if (flyFromSymbol) {
            this.spine.spine.skeleton.setupPose();
            this.spine.stop(0);
        }

        this.assignedWild.visible = true;
        if (!this.newSpinStarted) this.assignedWild.symbolAnimation.eventManager.playEventAnimation(ESymbolAnimations.WIN);

        this.assignedWild = undefined;
        this.assignedMultiplier = undefined;
        this.isBonusWheel = false;
    }

    public setStartingRotation(spine: SpineWrapper, multiplier: number, isWildAnimation: boolean): void {
        const wheelsConfig = this.gameStore.get(EGameStoreKeys.WildWheelConfiguration);
        const { wildMultipliersFromWheel, wheels } = wheelsConfig;
        const { multipliers, slotNameToRotate } = this.wildWheelConfig;
        const takeRotationFromDifferentWheel = isWildAnimation && wildMultipliersFromWheel;

        let bone = spine.spine.skeleton.findBone(slotNameToRotate);

        if (bone)
            bone.getPose().rotation = takeRotationFromDifferentWheel
                ? wheels[takeRotationFromDifferentWheel - 1].multipliers[multiplier].rotation
                : multipliers[multiplier].rotation;
    }

    public fly(toMiddle: boolean, correspondingWild: ReelSymbol): void {
        const wild = correspondingWild;
        if (!wild) return;

        const { flyFromSymbol } = this.wildWheelConfig;
        const { inDuration, outDuration } = flyFromSymbol ?? {};
        const duration = toMiddle ? inDuration : outDuration;
        if (duration === undefined) return;
        const wildWorldPos = wild.getGlobalPosition();
        if (toMiddle) this.spine.setPosition(wildWorldPos.x, wildWorldPos.y);

        const endX = toMiddle ? this.scene.screen.width / 2 : wildWorldPos.x;
        const endY = toMiddle ? this.scene.screen.height / 2 : wildWorldPos.y;

        const tweenConfig: gsap.TweenVars = {
            x: endX,
            y: endY,
            duration: duration / 1000,
            ease: 'quart.inOut',
            onComplete: () => {
                this.spine.setPosition(endX, endY);
            }
        };

        this.tween = gsap.to(this.spine.spine, tweenConfig);
    }

    public applyMultiplierToWild(): void {
        if (!(this.assignedWild && this.assignedMultiplier)) return;
        const wildAnimation = this.assignedWild.symbolAnimation;
        if (wildAnimation) this.applyMultiplier(wildAnimation, this.assignedMultiplier, true);
    }

    public applyMultiplier(spine: SpineWrapper, multiplier: number, isWildAnimation: boolean = false): void {
        const { multipliers } = this.wildWheelConfig;
        spine.spine.skeleton.setSkin(multipliers[multiplier].skinName);
        this.setStartingRotation(spine, multiplier, isWildAnimation);
    }

    private checkWild(): boolean {
        return this.manager.getWilds().length > 0 && !this.manager.getWildForInstance(this.assignedReelIndex);
    }
}
